import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Send, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { usePageMeta } from "@/lib/site";
import { relayApi, relTime } from "@/lib/accountApi";
import { worldOf } from "@/lib/worlds";
import { Empty } from "@/components/partner/PartnerShell";

const POLL_MS = 8000;

function otherName(c, me) {
  return c.provider_id === me?.id ? c.customer_name : c.provider_name || c.listing_title || "Provider";
}

function ConvRow({ c, me, active }) {
  const w = worldOf(c.module);
  const name = otherName(c, me);
  return (
    <Link to={`/messages/${c.id}`} data-testid={`conv-${c.id}`} className={`flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors hover:bg-muted/60 ${active ? "bg-muted" : ""}`}>
      {c.listing_image ? (
        <img src={c.listing_image} alt="" className="h-12 w-12 shrink-0 rounded-xl object-cover" />
      ) : (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-lg font-bold" style={{ background: w.accentSoft, color: w.accent }}>{(name || "?")[0]}</div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate font-semibold">{name}</p>
          <span className="shrink-0 text-xs text-muted-foreground">{relTime(c.last_message_at)}</span>
        </div>
        <p className="truncate text-xs text-muted-foreground">{c.listing_title}</p>
        <p className={`truncate text-sm ${c.unread ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{c.last_message || "Say hello"}</p>
      </div>
      {c.unread > 0 && <span className="rounded-full px-2 py-0.5 text-[11px] font-bold text-white" style={{ background: "var(--fable)" }}>{c.unread}</span>}
    </Link>
  );
}

function Thread({ id, me }) {
  const navigate = useNavigate();
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState(undefined);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottom = useRef(null);
  const count = useRef(0);

  const load = useCallback(async () => {
    try {
      const d = await relayApi.messages(id);
      setConv(d.conversation);
      setMsgs(d.messages || []);
      count.current = (d.messages || []).length;
    } catch (e) {
      toast.error(e.message || "Couldn't open this chat.");
      navigate("/messages", { replace: true });
    }
  }, [id, navigate]);

  useEffect(() => {
    setMsgs(undefined);
    load();
  }, [load]);

  // Delta polling like the app: a cheap /poll, and only fetch the thread when the count or typing state moved.
  useEffect(() => {
    const t = setInterval(async () => {
      if (document.hidden) return;
      try {
        const p = await relayApi.poll(id);
        if (p.message_count !== count.current) await load();
        else setConv((c) => (c ? { ...c, other_typing_at: p.other_typing_at, other_read_at: p.other_read_at } : c));
      } catch {
        /* next tick */
      }
    }, POLL_MS);
    return () => clearInterval(t);
  }, [id, load]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    setSending(true);
    try {
      const m = await relayApi.send(id, t);
      setMsgs((prev) => [...(prev || []), ...(Array.isArray(m?.messages) ? m.messages : [m?.message || m])].filter((x) => x && x.id));
      count.current += 1;
      setText("");
    } catch (err) {
      toast.error(err.message || "Couldn't send that.");
    } finally {
      setSending(false);
    }
  };

  const role = conv && conv.provider_id === me?.id ? "provider" : "customer";
  const typing = conv?.other_typing_at && Date.now() - new Date(conv.other_typing_at).getTime() < 8000;

  return (
    <div className="flex h-[70vh] min-h-[480px] flex-col rounded-2xl border border-border bg-card" data-testid="thread">
      <header className="flex items-center gap-3 border-b border-border px-4 py-3">
        <Link to="/messages" className="rounded-full p-1 hover:bg-muted lg:hidden" aria-label="Back"><ArrowLeft size={18} /></Link>
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold">{conv ? otherName(conv, me) : "…"}</p>
          <p className="truncate text-xs text-muted-foreground">
            {typing ? "typing…" : conv?.listing_title}
            {conv?.status === "ai" && !typing && " · Fable replies first, the provider steps in when needed"}
          </p>
        </div>
        {conv?.status === "ai" && role === "customer" && !conv?.closed && (
          <Button size="sm" variant="outline" className="rounded-full" data-testid="msg-human" onClick={async () => { try { await relayApi.escalate(id); toast.success("Asked the provider to step in"); load(); } catch (err) { toast.error(err.message || "Couldn't do that."); } }}>Talk to a human</Button>
        )}
        {conv?.listing_id && <Link to={`/listing/${conv.listing_id}`} className="text-xs font-medium text-primary hover:underline">View listing</Link>}
      </header>
      <div className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {msgs === undefined ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className={`h-10 w-2/3 rounded-2xl ${i % 2 ? "ml-auto" : ""}`} />)
        ) : msgs.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">No messages yet. Ask about availability, prices or anything else.</p>
        ) : (
          msgs.map((m) => {
            if (m.sender === "system") return <p key={m.id} className="py-1 text-center text-xs text-muted-foreground">{m.text}</p>;
            const mine = m.sender === role;
            const isAi = m.sender === "ai";
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm ${mine ? "rounded-br-md text-white" : "rounded-bl-md bg-muted"}`} style={mine ? { background: "var(--fable)" } : undefined}>
                  {!mine && <p className="mb-0.5 text-[11px] font-semibold text-muted-foreground">{isAi ? conv?.assistant_name || "Fable" : otherName(conv || {}, me)}</p>}
                  {m.type === "voice" ? <audio controls src={m.media?.url || m.audio_url} className="max-w-full" /> : m.media?.url ? <img src={m.media.url} alt="" className="max-h-48 rounded-xl" /> : <p className="whitespace-pre-wrap">{m.text}</p>}
                  <p className={`mt-1 text-[10px] ${mine ? "text-white/70" : "text-muted-foreground"}`}>{relTime(m.created_at)}{mine && conv?.other_read_at && conv.other_read_at >= m.created_at ? " · Seen" : ""}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottom} />
      </div>
      <form onSubmit={send} className="flex items-end gap-2 border-t border-border p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
          rows={1}
          maxLength={2000}
          placeholder={conv?.closed ? "This conversation is closed" : "Write a message…"}
          disabled={!!conv?.closed}
          data-testid="msg-input"
          className="max-h-32 flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
        />
        <Button type="submit" size="icon" className="rounded-full" disabled={sending || !text.trim() || !!conv?.closed} aria-label="Send" data-testid="msg-send"><Send size={16} /></Button>
      </form>
    </div>
  );
}

export default function Messages() {
  usePageMeta("Messages", "Your Relay chats with the providers you book on Fable +.");
  const { id } = useParams();
  const { user } = useAuth();
  const [convs, setConvs] = useState(undefined);

  const load = useCallback(() => relayApi.list().then(setConvs).catch(() => setConvs(null)), []);
  useEffect(() => {
    load();
    const t = setInterval(() => !document.hidden && load(), POLL_MS * 2);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="font-serif-display text-3xl font-bold">Messages</h1>
      <p className="mt-1 text-muted-foreground">Relay keeps every chat with a provider in one place. Fable answers the quick questions; the provider steps in for the rest.</p>
      <div className="mt-6 grid gap-6 lg:grid-cols-[340px_minmax(0,1fr)]">
        <aside className={`rounded-2xl border border-border bg-card p-2 ${id ? "hidden lg:block" : ""}`} data-testid="conv-list">
          {convs === undefined ? (
            <div className="space-y-2 p-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
          ) : convs === null ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Couldn't load your chats.</p>
          ) : convs.length === 0 ? (
            <div className="p-4"><Empty title="No chats yet" body="Message a provider from any listing and the conversation lands here." action={<Link to="/marketplace"><Button className="rounded-full">Browse the marketplace</Button></Link>} /></div>
          ) : (
            convs.map((c) => <ConvRow key={c.id} c={c} me={user} active={c.id === id} />)
          )}
        </aside>
        <section className={id ? "" : "hidden lg:block"}>
          {id ? (
            <Thread id={id} me={user} />
          ) : (
            <div className="flex h-[70vh] min-h-[480px] flex-col items-center justify-center rounded-2xl border border-dashed border-border text-center text-muted-foreground">
              <MessageSquare size={28} />
              <p className="mt-3 font-medium text-foreground">Pick a conversation</p>
              <p className="text-sm">Your messages show here.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
